/**
 * DisbursementArea — "What did Primelane pay out, what for, and at what rate?"
 *
 * THE PRINCIPAL IS NOT TYPED HERE, AND THAT IS THE SHAPE OF THE SCHEMA RATHER THAN A CHOICE OF
 * THIS CANVAS. `loans` carries no amount column at all: the money an advance states is the amount
 * of the DISBURSEMENT it names (`loans.transaction_id`, NOT NULL), so the selector is the money
 * field and the figure beside it is a read-out of the row that was picked. The side sheet this
 * replaces had an "Amount" box on its first step that was written nowhere and reconciled with
 * nothing — an admin could type 900 over a GHS 1,200 transaction and the Schedule would print 1,200.
 *
 * THE PURPOSE IS A BLOCK OF ITS OWN BECAUSE IT IS A DIFFERENT KIND OF FACT. The two rows above it
 * are the money as the bank recorded it; this is the sentence the borrower signs under "What this
 * advance is for", and it is the one field on this flow that is TYPED rather than picked or
 * derived. It is render-blocking — `agreement_merge_data` resolves `{{loan.purpose}}` through
 * `NULLIF(btrim(...), '')` — so a blank one holds the document rather than printing an empty line.
 *
 * FROZEN IS READ-ONLY, NOT DISABLED-LOOKING [F7]. Once the Schedule carries a signature its figures
 * cannot move underneath it, and the correction is void-and-reissue on the agreement area — which
 * the lock line names, because a field that simply stopped responding would be a defect as far as
 * anybody using it is concerned.
 */

import type { ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { formatCurrency } from '@/lib/format';

import { Block, Blocks, Hint, LockLine, Rec, Rows } from '../../areas/blocks';
import QuietField from '../../inline/QuietField';
import type { AdvanceFormValues } from '../advanceForm';
import { FROZEN_LOCK_BODY, FROZEN_LOCK_TITLE } from './lockCopy';

interface Props {
    mode: 'create' | 'edit';
    /** The Schedule is signed: every figure below is a read [F7]. */
    frozen: boolean;
    form: AdvanceFormValues;
    onChange: (updates: Partial<AdvanceFormValues>) => void;
    /** The edit commit bar for this area, drawn by the page while the block is dirty. */
    commit?: ReactNode;
}

export default function DisbursementArea({ mode, frozen, form, onChange, commit }: Props) {
    return (
        <Blocks>
            <Block id="b-disbursement" title="Disbursement" aside="required">
                {frozen && <LockLine title={FROZEN_LOCK_TITLE}>{FROZEN_LOCK_BODY}</LockLine>}

                <p className="sd-lede sd-dim">
                    The advance IS this payment. Its amount is what the Schedule prints as the
                    Amount advanced, and the interest is charged once on it — so picking the wrong
                    line here states the wrong money on a document the driver signs.
                </p>

                {frozen ? (
                    <Rows>
                        <Rec
                            label="Disbursement"
                            value={
                                form.amount === null
                                    ? form.transactionId
                                        ? `Transaction #${form.transactionId}`
                                        : null
                                    : formatCurrency(form.amount)
                            }
                            empty="No disbursement linked"
                        />
                        <Rec label="Interest rate" value={form.interestRate ? `${form.interestRate}%` : null} empty="Not set" />
                    </Rows>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <Label
                                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                                htmlFor="f-transaction"
                            >
                                Disbursement transaction
                            </Label>
                            <div id="f-transaction" tabIndex={-1}>
                                <TransactionSelector
                                    value={form.transactionId}
                                    /* THE SELECTOR HANDS BACK THE ROW IT PICKED, and the amount
                                       travels with the id: the proof-read has to state the money of
                                       the transaction that is about to be written, and a read of
                                       the server's copy would be a round trip behind the click. */
                                    onChange={(id, transaction) =>
                                        onChange({
                                            transactionId: id,
                                            amount: transaction?.amount ?? null,
                                        })
                                    }
                                    defaultType="DEBIT"
                                    placeholder="Select the payment this advance made…"
                                />
                            </div>
                            <Hint className="mt-2">
                                {form.amount === null
                                    ? 'The principal is the amount of the transaction picked here. Nothing on this page types it.'
                                    : `${formatCurrency(form.amount)} — the Amount advanced, as the Schedule will print it.`}
                            </Hint>
                        </div>

                        <div>
                            <Label
                                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                                htmlFor="f-rate"
                            >
                                Interest rate (%)
                            </Label>
                            <Input
                                id="f-rate"
                                type="number"
                                step="0.01"
                                min="0"
                                className="h-11"
                                value={form.interestRate}
                                onChange={(event) => onChange({ interestRate: event.target.value })}
                                placeholder="e.g. 10"
                            />
                            {/* ZERO IS A FIGURE, NOT AN ABSENCE. `loans.interest_rate` is NOT NULL
                                CHECK >= 0 and the Schedule prints "0 per cent" without complaint, so
                                an empty box is a deliberate interest-free advance rather than a gap
                                to chase. */}
                            <Hint className="mt-2">
                                Charged once, on the amount advanced. An empty box records 0 — an
                                interest-free advance is a real arrangement.
                            </Hint>
                        </div>
                    </div>
                )}
            </Block>

            <Block id="b-purpose" title="What this advance is for" aside="required">
                {frozen ? (
                    <Rows>
                        <Rec label="Purpose" value={form.purpose || null} empty="Not set" />
                    </Rows>
                ) : (
                    <>
                        <QuietField
                            id="f-purpose"
                            label="Purpose"
                            value={form.purpose}
                            onValueChange={(value) => onChange({ purpose: value })}
                            placeholder="What the money was for, as the borrower will read it"
                            format="prints on the Schedule"
                        />
                        <Hint className="mt-3">
                            The Schedule states this above the money, and will not render without
                            it. It is not the bank narration on the transaction — that line is
                            written for a statement, and this one is written for the borrower.
                        </Hint>
                    </>
                )}
            </Block>

            {mode === 'create' && !commit && (
                <Hint>
                    Nothing is written until the advance is created, and that happens on the
                    Repayment area — where the figures it will be billed at are stated.
                </Hint>
            )}

            {commit}
        </Blocks>
    );
}
